import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AnimatedCounter, StaggerChildren, StaggerItem } from '@/components/motion';
import {
  AlertTriangle,
  ArrowUpRight,
  Building2,
  CreditCard,
  FileText,
  Shield,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useAdminStore } from '../store/admin-store';

/**
 * El panel de la plataforma.
 *
 * 🔴 Lo que tenía era cuatro números planos —140 empresas, 253 usuarios, 133
 * suscripciones, $0— y el 60 % de la pantalla en blanco. Y cuatro números sueltos
 * **no responden ninguna pregunta de negocio**: 140 empresas puede ser magnífico o
 * pésimo según cuántas había el mes pasado.
 *
 * Lo que se agregó va todo en esa dirección:
 *
 *  · **Crecimiento por mes**, que es lo único que convierte un total en una
 *    tendencia.
 *  · **Quiénes vencen**, con nombre y días. Antes decía "4 suscripciones vencen
 *    en los próximos 7 días" — con ese número no se puede hacer nada sin ir a
 *    buscarlas de a una.
 *  · **Quiénes usan Prixia de verdad**, medido en cotizaciones. Una empresa
 *    activa que no cotiza es una empresa que se va a ir.
 *
 * Las animaciones son de entrada y de conteo, y una sola vez: un panel que se
 * mira diez veces por día no puede tener movimiento permanente.
 */

const COLORES_DE_PLAN: Record<string, string> = {
  TRIAL: '#94a3b8',
  BASIC: '#3b82f6',
  PROFESSIONAL: '#8b5cf6',
  ENTERPRISE: '#f59e0b',
};

interface TarjetaProps {
  titulo: string;
  valor: number;
  icono: React.ReactNode;
  detalle?: React.ReactNode;
  /** Cuántos se sumaron este mes. Es lo que convierte el total en tendencia. */
  esteMes?: number;
  prefijo?: string;
  href?: string;
}

function Tarjeta({ titulo, valor, icono, detalle, esteMes, prefijo, href }: TarjetaProps) {
  const contenido = (
    <Card className="h-full transition-colors hover:border-primary/40">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <p className="etiqueta-caps">{titulo}</p>
          <span className="text-muted-foreground" aria-hidden="true">
            {icono}
          </span>
        </div>

        {/* Bebas Neue condensada: entra mucho más grande sin romper la tarjeta. */}
        <p className="numero-grande mt-3 text-[44px]">
          <AnimatedCounter target={valor} prefix={prefijo} locale={valor >= 1000} />
        </p>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs">
          {esteMes !== undefined && esteMes > 0 && (
            <span className="flex items-center gap-0.5 font-medium text-emerald-600">
              <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
              {esteMes} este mes
            </span>
          )}
          {detalle && <span className="text-muted-foreground">{detalle}</span>}
        </div>
      </CardContent>
    </Card>
  );

  return href ? (
    <Link
      to={href}
      className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
    >
      {contenido}
    </Link>
  ) : (
    contenido
  );
}

export function AdminDashboardPage() {
  const { stats, statsLoading, fetchStats } = useAdminStore();

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const s: any = stats;
  const crecimiento = s?.crecimiento ?? [];
  const porVencer = s?.porVencer ?? [];
  const masActivas = s?.masActivas ?? [];
  const distribucion = s?.planDistribution ?? [];
  const totalConPlan = distribucion.reduce((t: number, d: any) => t + d.count, 0) || 1;

  return (
    <AppLayout variante="plataforma">
      <div className="container mx-auto space-y-6 p-4 md:p-8">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Shield className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <div>
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Plataforma</h1>
            <p className="text-sm text-muted-foreground">
              Cómo va la plataforma: quiénes entran, quiénes usan y quiénes se van
            </p>
          </div>
        </div>

        {statsLoading && !s ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : (
          <>
            {/* ── Los cuatro números, ahora con su tendencia ── */}
            <StaggerChildren className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StaggerItem>
                <Tarjeta
                  titulo="Empresas"
                  valor={s?.totalTenants ?? 0}
                  esteMes={s?.empresasEsteMes}
                  icono={<Building2 className="h-4 w-4" />}
                  detalle={`${s?.activeTenants ?? 0} activas`}
                  href="/plataforma/empresas"
                />
              </StaggerItem>
              <StaggerItem>
                <Tarjeta
                  titulo="Usuarios"
                  valor={s?.totalUsers ?? 0}
                  esteMes={s?.usuariosEsteMes}
                  icono={<Users className="h-4 w-4" />}
                  detalle={`${s?.activeUsers ?? 0} activos`}
                  href="/plataforma/personas"
                />
              </StaggerItem>
              <StaggerItem>
                <Tarjeta
                  titulo="Suscripciones activas"
                  valor={s?.activeSubscriptions ?? 0}
                  icono={<CreditCard className="h-4 w-4" />}
                  detalle={
                    s?.expiredSubscriptions > 0
                      ? `${s.expiredSubscriptions} vencidas`
                      : 'ninguna vencida'
                  }
                />
              </StaggerItem>
              <StaggerItem>
                <Tarjeta
                  titulo="Ingresos mensuales"
                  valor={s?.totalMonthlyRevenue ?? 0}
                  prefijo="$"
                  icono={<TrendingUp className="h-4 w-4" />}
                  detalle="estimado por planes activos"
                />
              </StaggerItem>
            </StaggerChildren>

            <div className="grid gap-4 lg:grid-cols-3">
              {/* ── Crecimiento ── */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Altas por mes</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Empresas y usuarios nuevos en los últimos seis meses
                  </p>
                </CardHeader>
                <CardContent>
                  {/*
                    Altura fija en el contenedor: `ResponsiveContainer` con altura
                    en porcentaje dentro de un padre sin altura colapsa a cero y el
                    gráfico no se dibuja — sin ningún error en consola.
                  */}
                  <div className="h-64 w-full">
                    {/*
                      `minWidth={0}` no es decoración: mientras la página hace su
                      transición de entrada, recharts mide el contenedor y le da -1,
                      y llena la consola de avisos aunque después dibuje bien. Es el
                      arreglo que la propia librería sugiere en ese aviso.
                    */}
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <AreaChart
                        data={crecimiento}
                        margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
                      >
                        <defs>
                          <linearGradient id="gradEmpresas" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradUsuarios" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.25} />
                            <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-border"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="etiqueta"
                          tickLine={false}
                          axisLine={false}
                          className="text-xs fill-muted-foreground"
                        />
                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          allowDecimals={false}
                          className="text-xs fill-muted-foreground"
                        />
                        <Tooltip
                          contentStyle={{
                            background: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: 6,
                            fontSize: 13,
                          }}
                          labelStyle={{ color: 'hsl(var(--foreground))' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="empresas"
                          name="Empresas"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          fill="url(#gradEmpresas)"
                        />
                        <Area
                          type="monotone"
                          dataKey="usuarios"
                          name="Usuarios"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          fill="url(#gradUsuarios)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* ── Distribución por plan ── */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Por plan</CardTitle>
                  <p className="text-sm text-muted-foreground">Empresas con suscripción activa</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {distribucion.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Ninguna empresa tiene plan asignado.
                    </p>
                  ) : (
                    distribucion.map((d: any) => {
                      const pct = Math.round((d.count / totalConPlan) * 100);
                      return (
                        <div key={d.planId} className="space-y-1.5">
                          <div className="flex items-baseline justify-between gap-2 text-sm">
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ background: COLORES_DE_PLAN[d.planTier] ?? '#94a3b8' }}
                                aria-hidden="true"
                              />
                              {d.planName}
                            </span>
                            <span className="tabular-nums text-muted-foreground">
                              {d.count} · {pct}%
                            </span>
                          </div>
                          {/* La barra crece al entrar: el porcentaje se lee antes de leerlo. */}
                          <div className="h-2 overflow-hidden rounded-full bg-muted">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.8, ease: 'easeOut' }}
                              className="h-full rounded-full"
                              style={{ background: COLORES_DE_PLAN[d.planTier] ?? '#94a3b8' }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {/* ── Por vencer ── */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
                    Vencen esta semana
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {/* Con nombre, no un número: un "4" no se puede accionar. */}
                    Con nombre y días, para poder llamarlos
                  </p>
                </CardHeader>
                <CardContent>
                  {porVencer.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Ninguna vence en los próximos 7 días.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {porVencer.map((v: any) => (
                        <li key={v.tenantId}>
                          <Link
                            to={`/plataforma/empresas/${v.tenantId}`}
                            className="flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/60"
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-medium">{v.tenantName}</span>
                              <span className="text-xs text-muted-foreground">{v.planName}</span>
                            </span>
                            <Badge variant={v.dias <= 2 ? 'destructive' : 'secondary'}>
                              {v.dias === 0 ? 'vence hoy' : `${v.dias} d`}
                            </Badge>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              {/* ── Quiénes usan de verdad ── */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    Quiénes más usan Prixia
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Medido en cotizaciones. Una empresa activa que no cotiza se va a ir.
                  </p>
                </CardHeader>
                <CardContent>
                  {masActivas.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Todavía no hay cotizaciones.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {masActivas.map((t: any, i: number) => (
                        <li key={t.id}>
                          <Link
                            to={`/plataforma/empresas/${t.id}`}
                            className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/60"
                          >
                            {/* La posición numera un orden real, no decora. */}
                            <span className="w-4 shrink-0 text-sm tabular-nums text-muted-foreground">
                              {i + 1}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">{t.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {t.usuarios} {t.usuarios === 1 ? 'usuario' : 'usuarios'}
                              </span>
                            </span>
                            <span className="shrink-0 text-sm tabular-nums">
                              {t.cotizaciones} cot.
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

export default AdminDashboardPage;
